import * as zod from "zod/v4";
import { InventoryPolicy } from "../draft-return/draft-return-items";
import { ProductDetails } from "../product2/product2-composite-types";
import { ReturnTypeEnum } from "../return";
import { isVariantInStock } from "../returns/products/product-variant-utils";
import { Settings } from "../team";

const VariantOptionSchema_ = zod.object({
  name: zod.string(),
  value: zod.string(),
  productAsVariant: zod.boolean().nullish(),
});
export const VariantOptionSchema: zod.ZodType<VariantOption> =
  VariantOptionSchema_;
export interface VariantOption extends zod.infer<typeof VariantOptionSchema_> {}

const PriceSchema_ = zod.object({
  amount: zod.string(),
  currency: zod.string(),
});
export const PriceSchema: zod.ZodType<Price> = PriceSchema_;
export interface Price extends zod.infer<typeof PriceSchema_> {}

const VariantImageSchema_ = zod.object({
  url: zod.string(),
  altText: zod.string().optional(),
});
export const VariantImageSchema: zod.ZodType<VariantImage> =
  VariantImageSchema_;
export interface VariantImage extends zod.infer<typeof VariantImageSchema_> {}

export const VariantMetafieldsSchema = zod
  .record(zod.string(), zod.string().nullish())
  .optional();
export type VariantMetafields = zod.infer<typeof VariantMetafieldsSchema>;

const VariantDataSchema_ = zod.object({
  variantId: zod.string(),
  productId: zod.string().nullish(),
  productTitle: zod.string().nullish(),
  variantTitle: zod.string(),
  price: PriceSchema,
  compareAtPrice: PriceSchema.optional(),
  image: VariantImageSchema,
  options: zod.array(VariantOptionSchema),
  onlineInventoryAvailable: zod.number(),
  // this can be true even if onlineInventoryAvailable is 0 depending on the merchant's settings
  // (e.g. if continue when out of stock is enabled)
  isInStock: zod.boolean(),
  backorder: zod.string().nullish(),
  sku: zod.string().nullish(),
  barcode: zod.string().nullish(),
  metafields: VariantMetafieldsSchema,
});
export const VariantDataSchema: zod.ZodType<VariantData> = VariantDataSchema_;
export interface VariantData extends zod.infer<typeof VariantDataSchema_> {}

const ProductVariantDataSchema_ = VariantDataSchema_.extend({
  productId: zod.string(),
  productTitle: zod.string(),
});
export const ProductVariantDataSchema: zod.ZodType<ProductVariantData> =
  ProductVariantDataSchema_;
export interface ProductVariantData
  extends zod.infer<typeof ProductVariantDataSchema_> {}

const ProductVariantDataWithQuantitySchema_ = ProductVariantDataSchema_.extend({
  quantity: zod.number(),
});
export const ProductVariantDataWithQuantitySchema: zod.ZodType<ProductVariantDataWithQuantity> =
  ProductVariantDataWithQuantitySchema_;
export interface ProductVariantDataWithQuantity
  extends zod.infer<typeof ProductVariantDataWithQuantitySchema_> {}

const ShopOnSiteProductDataSchema_ = zod.object({
  onlineStoreUrl: zod.string().optional(),
  handle: zod.string().optional(),
});
export const ShopOnSiteProductDataSchema: zod.ZodType<ShopOnSiteProductData> =
  ShopOnSiteProductDataSchema_;
export interface ShopOnSiteProductData
  extends zod.infer<typeof ShopOnSiteProductDataSchema_> {}

const ProductOptionSchema_ = zod.object({
  name: zod.string(),
  values: zod.array(zod.string()),
});
export const ProductOptionSchema: zod.ZodType<ProductOption> =
  ProductOptionSchema_;
export interface ProductOption extends zod.infer<typeof ProductOptionSchema_> {}

const ProductImageSchema_ = zod.object({
  url: zod.string(),
  alt: zod.string().nullish(),
});
export const ProductImageSchema: zod.ZodType<ProductImage> =
  ProductImageSchema_;
export interface ProductImage extends zod.infer<typeof ProductImageSchema_> {}

const ProductDataSchema_ = zod.object({
  productId: zod.string(),
  productTitle: zod.string(),
  options: zod.array(ProductOptionSchema),
  images: zod.array(ProductImageSchema).nullish(),
  variants: zod.array(VariantDataSchema),
  shopOnSite: ShopOnSiteProductDataSchema.optional(),
});
export const ProductDataSchema: zod.ZodType<ProductData> = ProductDataSchema_;
export interface ProductData extends zod.infer<typeof ProductDataSchema_> {}

/**
 * Converts a Product2WithAttributes to ProductData for use in the customer portal.
 */
export function convertProduct2ToProductData(
  product: ProductDetails,
  shopCurrency = "USD",
  inventorySettings: Settings["inventory"],
  returnType: ReturnTypeEnum,
): ProductData {
  // Build a map of attribute value IDs to their names and parent attribute names
  const attributeValueMap = new Map<
    string,
    { attributeName: string; valueName: string }
  >();
  const optionsMap = new Map<string, Set<string>>();

  for (const attribute of product.attributes) {
    optionsMap.set(attribute.name, new Set());
    for (const value of attribute.values) {
      attributeValueMap.set(value._id.toString(), {
        attributeName: attribute.name,
        valueName: value.string ?? "",
      });
    }
  }

  // Build product images
  const productImages: ProductImage[] = product.images.map((img) => ({
    url: img.url,
    alt: img.altText,
  }));

  // Find the first product image for variants without specific images
  const defaultImage: VariantImage = productImages[0]
    ? { url: productImages[0].url, altText: productImages[0].alt ?? undefined }
    : { url: "", altText: undefined };

  // Convert variants
  const variants: VariantData[] = product.variants.map((variant) => {
    // Build variant options from attribute values
    const variantOptions: VariantOption[] = [];
    for (const attrValueId of variant.attributeValues) {
      const attrValue = attributeValueMap.get(attrValueId.toString());
      if (attrValue) {
        variantOptions.push({
          name: attrValue.attributeName,
          value: attrValue.valueName,
        });
        optionsMap.get(attrValue.attributeName)?.add(attrValue.valueName);
      }
    }

    // Build variant title from options
    const variantTitle =
      variantOptions.map((opt) => opt.value).join(" / ") || "Default";

    return {
      variantId: variant.externalId ?? variant._id.toString(),
      productId: product.externalId ?? product._id.toString(),
      productTitle: product.name,
      variantTitle,
      price: {
        amount: variant.price?.toString() ?? "0",
        currency: shopCurrency,
      },
      image: defaultImage,
      options: variantOptions,
      onlineInventoryAvailable: variant.available,
      isInStock: isVariantInStock({
        inventorySettings,
        returnType,
        inventoryQuantity: variant.available,
        inventoryTracked: variant.inventoryTracked,
        inventoryPolicy: variant.sellWhenOutOfStock
          ? InventoryPolicy.CONTINUE
          : InventoryPolicy.DENY,
      }),
      sku: variant.sku,
      metafields: undefined,
    };
  });

  // Build product-level options
  const options: ProductOption[] = [];
  for (const [name, values] of optionsMap) {
    if (values.size > 0) {
      options.push({ name, values: Array.from(values) });
    }
  }

  return {
    productId: product.externalId ?? product._id.toString(),
    productTitle: product.name,
    options,
    images: productImages.length > 0 ? productImages : null,
    variants,
  };
}
